import type { LonLat } from "utils/curveLine";
import type { NextStops, PartnerMapContent, Tour, WeekEvent, WeekProgram } from "../maps/types";
import type { PartnerListData } from "../shared/liveList";
import { compassWord, distanceKm } from "../maps/walk";
import { weekdayIndex } from "../maps/weekProgram";
import { COUNTRIES, getHostel, HOSTELS, MAP_HOSTELS } from "./content";
import { mmLink, mmUrl } from "./links";

/**
 * What a Mad Monkey hostel's map takes from the data the server loaded for it (`/p/mad-monkey-<hostel>`, loader `partnerMap`):
 * its program, from the knowledge base's `explore/partner-programs/` by way of the `partner_programs` collection, laid over
 * the hostel's content (`hostelMaps.ts`) the way its synced list is (`withLiveList`). Without a program the content is the
 * hostel as it was: no Week program, the info it has on its own.
 *
 *   weekly      the events of the Week program, on their weekdays, and the "Today at the hostel" row
 *   comingUp    one-night events: on their own date, and in the "Coming up" strip while they are still to come
 *   tours       a "Tours" list under the day
 *   info        the "Book & info" panel and the block that ends the Highlights tab
 *   socials     "Follow the chaos"
 *   nextStops   "Don't forget your next Mad Monkey": the hostels on, resolved to the hostel data for name, photo and place, and under
 *               them the route of the hostel's region on Stippl (`ROUTE_PLANS`)
 *
 * An event or a tour may have a photo (`image`): beside the event in the day's list, in the icon square of the "Coming up" and
 * "Tours" cards, and as the picture of the event's sheet. A tour may have a `group`: the variants that share one are listed
 * under one row (`WeekTab`).
 *
 * No prices: the program has none, and nothing here would show one. A link to madmonkeyhostels.com carries the same utm as every
 * other link from these pages to Mad Monkey (`mmLink`).
 */

// Mirrors `PartnerProgramData` / `PartnerMapData` in graphql/helpers/partnerMapLoader.ts: change both.
export interface PartnerProgramData {
	info: { book?: string; events?: string; website?: string; address?: string; directions?: string };
	/** `days`: 0 = Monday ... 6 = Sunday, `[]` when the hostel does not say. `time`: "" or starts joined by " · ", "19:00 · 19:30": a `WeekEvent.time` as it is */
	weekly: { days: number[]; time: string; title: string; line: string; url?: string; image?: string }[];
	/** `date`: YYYY-MM-DD */
	comingUp: { date: string; time: string; title: string; line: string; url?: string; image?: string }[];
	/** `group`: the bundle a tour is listed under with its variants, absent for a tour on its own */
	tours: { title: string; kind: string; duration: string; days: number[]; line: string; url?: string; image?: string; group?: string }[];
	/** `hostel`: the knowledge-base place, `cambodia/siem-reap/mad-monkey-siem-reap` */
	nextStops: { hostel: string; line: string; url?: string }[];
	socials: { network: string; url: string }[];
}

export type PartnerMapData = PartnerListData & { program?: PartnerProgramData };

const SLUG_PREFIX = "mad-monkey-";

/** The small print under the day's events. */
const FINEPRINT = "Everything starts at the hostel bar unless it says otherwise. Book a spot on Mad Monkey's own event pages, or just turn up.";

/** What an event's drawing is, by what it is called: the first of these that matches, else a ticket. Marker drawings (`shared/markerIcons.json`). */
const EVENT_ICONS: [RegExp, string][] = [
	[/quiz|bingo|trivia/i, "star"],
	[/pool|volleyball|football|olympics|games?\b/i, "sports"],
	[/beer ?pong|pub crawl|karaoke|party|cocktail|happy hour|shots/i, "cocktail"],
	[/dinner|breakfast|brunch|lunch|bbq|barbecue|cook|noodle|pancake/i, "eat"],
	[/coffee/i, "coffee"],
	[/hike|trek|sunrise|sunset|waterfall/i, "mountain"],
	[/meet ?up|bar\b|night|drinks?/i, "cocktail"],
];
const DEFAULT_EVENT_ICON = "ticket";

export const iconForEvent = (title: string): string => EVENT_ICONS.find(([pattern]) => pattern.test(title))?.[1] ?? DEFAULT_EVENT_ICON;

const countWords = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];

/** The program the loader sent for this map, or null: other data was loaded for another page, or there is none. */
function programOf(data: unknown, slug: string): PartnerProgramData | null {
	const loaded = data as Partial<PartnerMapData> | null | undefined;
	return loaded && typeof loaded === "object" && loaded.slug === slug && loaded.program && typeof loaded.program === "object" ? loaded.program : null;
}

const booking = (url?: string): Pick<WeekEvent, "booking"> => (url ? { booking: { url: mmLink(url), cta: "Sign up" } } : {});

/** The program's photo of an event or a tour, when it has one (the loader has let https through only). */
const image = (url?: string): Pick<WeekEvent, "image"> => (url ? { image: url } : {});

/** The Week program of a program: weekly events on their days (one with no days cannot be placed on one), one-night events on their dates, the tours. */
function weekProgramOf(program: PartnerProgramData): WeekProgram | undefined {
	// by their first start, the ones with no start last: a day reads in time order whatever order the file wrote them in
	const start = (time: string) => time.split(" · ")[0] || "99:99";
	const weekly = [...program.weekly]
		.filter((e) => e.days.length > 0)
		.sort((a, b) => start(a.time).localeCompare(start(b.time)))
		.map(
			(e, i): WeekEvent => ({
				id: `weekly-${i}`,
				time: e.time,
				title: e.title,
				note: e.line,
				price: null,
				where: "hostel",
				icon: iconForEvent(e.title),
				days: e.days,
				tonight: e.title,
				...booking(e.url),
				...image(e.image),
			})
		);
	const oneNight = program.comingUp.map(
		(e, i): WeekEvent => ({
			id: `up-${i}`,
			time: e.time,
			title: e.title,
			note: e.line,
			price: null,
			where: "hostel",
			icon: iconForEvent(e.title),
			// noon, so that no time zone moves the date: the weekday of the date as written
			days: [weekdayIndex(new Date(`${e.date}T12:00`))],
			date: e.date,
			...booking(e.url),
			...image(e.image),
		})
	);
	const tours = program.tours.map(
		(t, i): Tour => ({
			id: String(i),
			title: t.title,
			note: t.line,
			kind: t.kind,
			duration: t.duration,
			days: t.days,
			...(t.url ? { url: mmLink(t.url) } : {}),
			...image(t.image),
			...(t.group ? { group: t.group } : {}),
		})
	);
	const events = [...weekly, ...oneNight];
	return events.length || tours.length ? { tonightLabel: "Today at the hostel", events, tours, fineprint: FINEPRINT } : undefined;
}

/** "580 km north of Bangkok": the way on from the base, to the 10 km. */
const distanceText = (from: LonLat, to: LonLat, baseName: string): string =>
	`${Math.max(10, Math.round(distanceKm(from, to) / 10) * 10)} km ${compassWord(from, to)} of ${baseName}`;

/** The hostel a next stop names: by its knowledge-base place (`.../mad-monkey-siem-reap`) or by the `/destination/<slug>` of its link; null when it is none of ours. */
function stopSlug(stop: { hostel: string; url?: string }): string | null {
	const ref = stop.hostel.split("/").pop() ?? "";
	const byRef = ref.startsWith(SLUG_PREFIX) ? ref.slice(SLUG_PREFIX.length) : "";
	let byUrl = "";
	try {
		byUrl = /\/destination\/([a-z0-9-]+)\/?$/.exec(new URL(stop.url ?? "").pathname)?.[1] ?? "";
	} catch {
		// not a link: the place alone decides
	}
	return [byRef, byUrl].find((slug) => slug && getHostel(slug)) ?? null;
}

/**
 * The line under the next stops: the route of a hostel's region on Stippl, the designer's table. `trip` is the route's synced
 * Stippl trip, the same ones as the route cards (`madMonkeyData.json`); a hostel in none of these has no line.
 */
const ROUTE_PLANS: { hostels: string[]; lead: string; text: string; trip: string; /** utm_content, when not `route_<country>` */ content?: string }[] = [
	{
		// the whole trail for every hostel on it (Ilse, 19 Oct), not the route of the hostel's country
		hostels: ["bangkok", "chiang-mai", "pai", "luang-prabang", "vang-vieng", "hanoi", "hoi-an", "siem-reap", "phnom-penh", "koh-rong"],
		lead: "On the Banana Pancake Trail?",
		text: "Ten Mad Monkeys from Bangkok to Koh Rong in a month: get the route, free.",
		trip: "6ad283fd60b3421e1732f913",
		content: "route_banana_pancake",
	},
	{
		hostels: ["phuket"],
		lead: "Doing the whole loop?",
		text: "Bangkok, Chiang Mai, Pai and Phuket in 2 weeks: get the route, free.",
		trip: "6ad283fe60b3421e1732f91f",
	},
	{
		hostels: ["koh-sdach"],
		lead: "Doing the whole loop?",
		text: "Siem Reap to the islands in 2 weeks: get the route, free.",
		trip: "6ad283fe60b3421e1732f919",
	},
	{
		hostels: ["uluwatu", "nusa-lembongan", "gili-trawangan", "kuta-lombok"],
		lead: "Doing the whole loop?",
		text: "Bali, Gili T and Lombok in 12 nights: get the route, free.",
		trip: "6ad283fe60b3421e1732f925",
	},
	{
		hostels: ["manila", "nacpan-beach", "panglao", "siquijor", "dumaguete", "siargao"],
		lead: "Island hopping?",
		text: "Manila to Siargao in 16 nights: get the route, free.",
		trip: "6ad283ff60b3421e1732f92c",
	},
];

/** The route line under a hostel's next stops, or undefined for a hostel with none. `save=1` takes the visitor straight to the login and the copy (RoutePreview). The utm is the designer's, not the page's other links' (`mmLink`, `utmParams`). */
export function routePlanOf(slug: string): NextStops["plan"] {
	const route = ROUTE_PLANS.find(({ hostels }) => hostels.includes(slug));
	if (!route) return undefined;
	const country = COUNTRIES[HOSTELS[slug].cc].toLowerCase();
	return {
		lead: route.lead,
		text: route.text,
		url: `https://www.stippl.io/routepreview/${route.trip}?save=1&utm_source=madmonkey&utm_medium=qr&utm_campaign=${slug}&utm_content=${route.content ?? `route_${country}`}`,
	};
}

/** "Don't forget your next Mad Monkey": the stops that are hostels of ours, other than this one, in the program's order. */
function nextStopsOf(program: PartnerProgramData, baseSlug: string): NextStops | undefined {
	const base = HOSTELS[baseSlug];
	const from: LonLat = [base.lon, base.lat];
	const found = program.nextStops.flatMap((stop) => {
		const slug = stopSlug(stop);
		return slug && slug !== baseSlug ? [{ slug, stop }] : [];
	});
	if (!found.length) return undefined;
	const count = `${countWords[found.length] ?? found.length} more ${found.length === 1 ? "stop" : "stops"}`;
	// "in Thailand" only when every one is: a stop over the border would make it a lie
	const domestic = found.every(({ slug }) => HOSTELS[slug].cc === base.cc);
	return {
		lead: "Don't forget your",
		em: "next Mad Monkey",
		subtitle: `Same crew, same Meet Up every night, ${count}${domestic ? ` in ${COUNTRIES[base.cc]}` : " down the trail"}.`,
		stops: found.map(({ slug, stop }) => ({
			name: `Mad Monkey ${HOSTELS[slug].name}`,
			line: stop.line || HOSTELS[slug].tag,
			photo: HOSTELS[slug].photos[0],
			url: mmLink(stop.url ?? mmUrl(slug)),
			distance: distanceText(from, [HOSTELS[slug].lon, HOSTELS[slug].lat], base.name),
		})),
		plan: routePlanOf(baseSlug),
	};
}

/**
 * A Mad Monkey hostel's map with its program laid over it, and its subtitle counting what it shows. Any other page's content,
 * and data loaded for another page, come back as they are; a map with no program keeps the hostel's own info and has no Week program.
 */
export function withHostelData(content: PartnerMapContent, data: unknown): PartnerMapContent {
	const hostel = content.slug.startsWith(SLUG_PREFIX) ? content.slug.slice(SLUG_PREFIX.length) : "";
	if (!MAP_HOSTELS.includes(hostel)) return content;

	const program = programOf(data, content.slug);
	const weekProgram = program && weekProgramOf(program);
	const picks = content.highlights.length;
	// no highlight (a hostel whose list holds only the hostel) says only what it has: the nights, else the hostel's own tagline
	const subtitle = picks
		? `${picks} ${picks === 1 ? "highlight" : "highlights"} picked by the crew${weekProgram ? ", plus what's on at the hostel every night" : ""}`
		: weekProgram
			? "What's on at the hostel every night"
			: HOSTELS[hostel].tag;
	if (!program) return { ...content, header: { ...content.header, subtitle } };

	const { info } = program;
	return {
		...content,
		header: { ...content.header, subtitle },
		...(weekProgram ? { weekProgram } : {}),
		info: {
			...content.info!,
			...(info.book ? { book: mmLink(info.book) } : {}),
			...(info.events ? { events: mmLink(info.events) } : {}),
			...(info.website ? { website: mmLink(info.website) } : {}),
			...(info.directions ? { directions: info.directions } : {}),
			...(info.address ? { address: info.address } : {}),
			socials: program.socials,
		},
		nextStops: nextStopsOf(program, hostel),
	};
}
